import { ACADEMIA_PRICING_BY_CATEGORY } from '../constants/academiaPricing';

export type TarifFormationRow = {
  id: string;
  categorie: string;
  typeFrais: string;
  montantCdf?: number | string;
  referenceUsd?: number | string | null;
};

export type PublicTarifCategoryDisplay = {
  id: (typeof CATEGORIE_ORDER)[number];
  label: string;
  age: string;
  inscription: string;
  participation: string;
  brevet: string;
  highlight?: boolean;
};

const CATEGORIE_ORDER = ['JEUNESSE_PRIORITAIRE', 'ADULTE_STANDARD', 'FONCTIONNAIRE_EMPLOYE'] as const;

const CATEGORIE_LABELS: Record<(typeof CATEGORIE_ORDER)[number], string> = {
  JEUNESSE_PRIORITAIRE: 'Jeunesse prioritaire',
  ADULTE_STANDARD: 'Adulte standard',
  FONCTIONNAIRE_EMPLOYE: 'Fonctionnaires & employés',
};

const CATEGORIE_AGE: Record<(typeof CATEGORIE_ORDER)[number], string> = {
  JEUNESSE_PRIORITAIRE: '17 – 29 ans',
  ADULTE_STANDARD: '30 ans et +',
  FONCTIONNAIRE_EMPLOYE: 'Secteur public & privé',
};

const TYPE_ORDER = ['INSCRIPTION', 'PARTICIPATION', 'BREVET'] as const;

function formatCdf(amount: number): string {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(amount)} CDF`;
}

function formatUsd(amount: number): string {
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(amount)} USD`;
}

function formatTarifCell(row: TarifFormationRow | undefined): string {
  if (!row) return '—';
  const usd = row.referenceUsd != null && row.referenceUsd !== '' ? Number(row.referenceUsd) : NaN;
  const cdf = Number(row.montantCdf ?? 0);

  if (row.typeFrais === 'BREVET' && Number.isFinite(usd) && usd > 0) {
    return formatUsd(usd);
  }
  if (
    row.typeFrais === 'INSCRIPTION' &&
    row.categorie === 'FONCTIONNAIRE_EMPLOYE' &&
    Number.isFinite(usd) &&
    usd > 0
  ) {
    return formatUsd(usd);
  }
  if (Number.isFinite(cdf) && cdf > 0) {
    return formatCdf(cdf);
  }
  if (Number.isFinite(usd) && usd > 0) {
    return formatUsd(usd);
  }
  return '—';
}

/** Regroupe les tarifs API en grille par catégorie (montants dynamiques admin). */
export function groupPublicTarifsByCategory(rows: TarifFormationRow[]): PublicTarifCategoryDisplay[] {
  const byKey = new Map<string, TarifFormationRow>();
  for (const row of rows) {
    byKey.set(`${row.categorie}:${row.typeFrais}`, row);
  }

  return CATEGORIE_ORDER.map((id) => {
    const pick = (typeFrais: (typeof TYPE_ORDER)[number]) => byKey.get(`${id}:${typeFrais}`);

    return {
      id,
      label: CATEGORIE_LABELS[id],
      age: CATEGORIE_AGE[id],
      inscription: formatTarifCell(pick('INSCRIPTION')),
      participation: formatTarifCell(pick('PARTICIPATION')),
      brevet: formatTarifCell(pick('BREVET')),
      highlight: id === 'JEUNESSE_PRIORITAIRE',
    };
  });
}

/** Référence USD du brevet (première ligne BREVET avec referenceUsd). */
export function getBrevetUsdReference(rows: TarifFormationRow[]): number | null {
  for (const row of rows) {
    if (row.typeFrais !== 'BREVET') continue;
    const usd = row.referenceUsd != null && row.referenceUsd !== '' ? Number(row.referenceUsd) : NaN;
    if (Number.isFinite(usd) && usd > 0) return usd;
  }
  return null;
}

/** Grille statique de secours si l’API est indisponible. */
export function fallbackPublicTarifCategories(): PublicTarifCategoryDisplay[] {
  return ACADEMIA_PRICING_BY_CATEGORY.map((row) => ({
    id: row.id,
    label: CATEGORIE_LABELS[row.id],
    age: CATEGORIE_AGE[row.id],
    inscription: row.inscription,
    participation: row.participation,
    brevet: row.brevet,
    highlight: row.id === 'JEUNESSE_PRIORITAIRE',
  }));
}

export function buildBrevetPricingHint(rows: TarifFormationRow[]): string {
  const usd = getBrevetUsdReference(rows);
  if (usd != null) {
    return `Le brevet certifiant est facturé séparément des frais ci-dessous : équivalent ${formatUsd(usd)}, converti en CDF au taux du jour.`;
  }
  const brevetRow = rows.find((r) => r.typeFrais === 'BREVET');
  if (brevetRow) {
    const label = formatTarifCell(brevetRow);
    if (label !== '—') {
      return `Le brevet certifiant est facturé séparément des frais ci-dessous : ${label}.`;
    }
  }
  return 'Le brevet certifiant est facturé séparément ; son montant figure dans la grille tarifaire en vigueur.';
}
